import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "../../../store";
import { Box, Grid2, Typography } from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import FormAutocomplete from "../../forms/FormAutocomplete";
import {
  fetchConnectionTypes,
  fetchSystemTypes,
} from "../../../slices/connection";
import { useSkeletonLoader } from "../../../hooks/useSkeletonLoader";
import Settings from "../../../types/settings";
import ConnectionType from "../../../types/connectionType";

interface Props {
  onSave: SubmitHandler<Settings>;
}

export type SettingsFormSubmitHandler = () => void;

const SettingsForm = forwardRef<SettingsFormSubmitHandler, Props>(
  (props, ref) => {
    const { onSave } = props;
    const dispatch = useDispatch();

    const { systemTypes, formStatus, connectionTypes } = useSelector(
      (store) => store.connection
    );
    const { settings, status } = useSelector((store) => store.settings);

    const { handleSubmit, control, watch, reset, setValue } = useForm<Settings>(
      {
        mode: "onChange",
        defaultValues: settings,
      }
    );

    const formValues = watch();

    const { loadWithSkeleton } = useSkeletonLoader(
      formStatus === "loading" || status === "loading"
    );
    useImperativeHandle(ref, () => handleSubmit(onSave));

    useEffect(() => {
      dispatch(fetchSystemTypes());
      dispatch(fetchConnectionTypes());
    }, [dispatch]);

    useEffect(() => {
      if (!!settings) {
        reset(settings);
      }
    }, [reset, settings]);

    const handleSystemTypeChange = (connectionTypes: ConnectionType[]) => {
      let enabledSystems = formValues.enabledSystems?.filter((x) =>
        connectionTypes?.some((c) => c?.id === x.connectionType?.id)
      );
      setValue("enabledSystems", enabledSystems);
    };

    return (
      <Box display="flex" flexDirection="row" width="100%">
        <Box display="flex" flexDirection="column" width="100%">
          <form>
            <Grid2 container spacing={2}>
              <Box display="flex" flexDirection="row" width="100%" marginY={1}>
                <Typography fontWeight="bold">Connection Options</Typography>
              </Box>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="enabledConnectionTypes"
                    options={connectionTypes || []}
                    required
                    label="Enabled System Types"
                    multiple
                    onChange={handleSystemTypeChange}
                    staticFilter
                  />
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                {loadWithSkeleton(
                  <FormAutocomplete
                    control={control}
                    fieldName="enabledSystems"
                    options={
                      systemTypes?.filter((x) =>
                        formValues.enabledConnectionTypes?.some(
                          (c) => c.id === x.connectionType?.id
                        )
                      ) || []
                    }
                    required
                    label="Connected Systems"
                    defaultValue={formValues.enabledSystems || []}
                    noOptionsText={
                      !formValues.enabledConnectionTypes
                        ? "Select enabled system types to load systems"
                        : "No options available"
                    }
                    multiple
                    staticFilter
                  />
                )}
              </Grid2>
            </Grid2>
          </form>
        </Box>
      </Box>
    );
  }
);

export default SettingsForm;
